import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import {
  C,
  TIP_COLORS,
  BAR_COLORS,
  cardStyle,
  SecTitle,
  BLabel,
  abreviarPersona,
  fichaPct,
  type FilterKey,
  type Filters,
  type DashboardProceso,
  type DashboardElement,
} from './dashboard-utils';

interface ReprDatum {
  name: string;
  value: number;
}

interface DashboardTablesProps {
  tableReprData: ReprDatum[];
  tableProcs: DashboardProceso[];
  filteredElements: DashboardElement[];
  allProcs: DashboardProceso[];
  flt: Filters;
  has: boolean;
  activePills: Array<{ key: FilterKey; value: string; label: string }>;
  tog: (k: FilterKey, v: string) => void;
  dim: (k: FilterKey, v: string) => React.CSSProperties;
  removePill: (k: FilterKey, v: string) => void;
  clr: () => void;
  navToLevel3: (processId: string) => void;
  navToDocInLevel3: (processId: string, docCodigo: string) => void;
}

export function DashboardTables({
  tableReprData,
  tableProcs,
  filteredElements,
  allProcs,
  flt,
  has,
  activePills,
  tog,
  dim,
  removePill,
  clr,
  navToLevel3,
  navToDocInLevel3,
}: DashboardTablesProps) {
  return (
    <>
      {/* ══ ROW 4: Filter Pills ══ */}
      {has ? (
        <div
          style={{
            gridColumn: '1 / -1',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'center',
            maxHeight: 24,
            overflow: 'hidden',
          }}
        >
          {activePills.map((pill, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                background: C.as,
                color: C.a1,
                fontSize: 9,
                fontWeight: 700,
                padding: '1px 7px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
              }}
            >
              {pill.label}
              <button
                onClick={() => removePill(pill.key, pill.value)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.a1,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 900,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                &times;
              </button>
            </span>
          ))}
          <button
            onClick={clr}
            style={{
              background: C.ce,
              color: C.ao,
              border: 'none',
              borderRadius: 8,
              padding: '1px 8px',
              fontSize: 9,
              fontWeight: 800,
              cursor: 'pointer',
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <div style={{ gridColumn: '1 / -1' }} />
      )}

      {/* ══ ROW 5: 3 bottom cards — Representantes | Procesos | Documentos ══ */}

      {/* Representantes */}
      <div style={{ ...cardStyle, padding: '6px 8px' }}>
        <SecTitle>Representantes</SecTitle>
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={tableReprData.slice(0, 15)}
              layout="vertical"
              margin={{ left: 180, right: 20, top: 4, bottom: 2 }}
            >
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={180}
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                interval={0}
                tickFormatter={(v: string) => abreviarPersona(v, 40)}
              />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
              <Bar
                dataKey="value"
                label={<BLabel />}
                radius={[0, 3, 3, 0]}
                barSize={8}
                onClick={(_: unknown, idx: number) => {
                  const item = tableReprData[idx];
                  if (item) tog('representante', item.name);
                }}
                style={{ cursor: 'pointer' }}
              >
                {tableReprData.slice(0, 15).map((entry, i) => (
                  <Cell
                    key={i}
                    fill={BAR_COLORS[i % BAR_COLORS.length]}
                    style={dim('representante', entry.name)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Procesos table */}
      <div style={{ ...cardStyle, padding: '6px 8px' }}>
        <SecTitle>Procesos</SecTitle>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <table
            className="pd-table"
            style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
          >
            <thead>
              <tr style={{ background: C.ao, color: C.w, position: 'sticky', top: 0, zIndex: 1 }}>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 52,
                  }}
                >
                  Codigo
                </th>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Nombre
                </th>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 28,
                  }}
                >
                  Tip
                </th>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 30,
                  }}
                >
                  Doc
                </th>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 36,
                  }}
                >
                  %FC
                </th>
                <th style={{ padding: '3px 4px', width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {tableProcs.map((p, i) => {
                const fp = fichaPct(p);
                const rowOpacity = flt.proceso.size > 0 && !flt.proceso.has(p.codigo) ? 0.18 : 1;
                return (
                  <tr
                    key={p.id}
                    onClick={() => tog('proceso', p.codigo)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: `1px solid ${C.gc}`,
                      background: i % 2 === 1 ? '#FAFAFA' : 'transparent',
                      opacity: rowOpacity,
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = '#F0F4FF';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background =
                        i % 2 === 1 ? '#FAFAFA' : 'transparent';
                    }}
                  >
                    <td
                      style={{
                        padding: '2px 4px',
                        fontWeight: 700,
                        fontSize: 10,
                        fontFamily: "'JetBrains Mono',monospace",
                        color: C.a2,
                      }}
                    >
                      {p.codigo}
                    </td>
                    <td style={{ padding: '2px 4px', fontSize: 10, color: C.b }}>{p.nombre}</td>
                    <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0 4px',
                          borderRadius: 6,
                          background: TIP_COLORS[p.tipologia] ?? C.go,
                          color: '#fff',
                          fontSize: 8,
                          fontWeight: 800,
                        }}
                      >
                        {p.tipologia}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '2px 4px',
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: 10,
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      {p.totalDocumentos}
                    </td>
                    <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                      <span
                        style={{
                          fontWeight: 800,
                          fontSize: 10,
                          fontFamily: "'JetBrains Mono',monospace",
                          color: fp >= 80 ? C.statusOk : fp >= 40 ? C.statusWarn : C.statusError,
                        }}
                      >
                        {fp}%
                      </span>
                    </td>
                    <td
                      style={{ textAlign: 'center', padding: '4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navToLevel3(p.id);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={C.a2}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ cursor: 'pointer', opacity: 0.7 }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as SVGSVGElement).style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as SVGSVGElement).style.opacity = '0.7';
                        }}
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Documentos table */}
      <div style={{ ...cardStyle, padding: '6px 8px' }}>
        <SecTitle>Documentos</SecTitle>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <table
            className="pd-table"
            style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
          >
            <thead>
              <tr style={{ background: C.ao, color: C.w, position: 'sticky', top: 0, zIndex: 1 }}>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 62,
                  }}
                >
                  Codigo
                </th>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Nombre
                </th>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 44,
                  }}
                >
                  Proc
                </th>
                <th
                  style={{
                    padding: '3px 4px',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 50,
                  }}
                >
                  Estado
                </th>
                <th style={{ padding: '3px 4px', width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {filteredElements.map((d, i) => {
                const isPubl = d.estado === 'DIGITALIZADO' || d.estado === 'DOCUMENTADO';
                const proc = allProcs.find((p) => p.id === d.procesoId);
                return (
                  <tr
                    key={`${d.codigo}-${i}`}
                    onClick={() => {
                      if (proc) tog('proceso', proc.codigo);
                    }}
                    style={{
                      cursor: 'pointer',
                      borderBottom: `1px solid ${C.gc}`,
                      background: i % 2 === 1 ? '#FAFAFA' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = '#F0F4FF';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background =
                        i % 2 === 1 ? '#FAFAFA' : 'transparent';
                    }}
                  >
                    <td
                      style={{
                        padding: '2px 4px',
                        fontWeight: 700,
                        fontSize: 10,
                        fontFamily: "'JetBrains Mono',monospace",
                        color: C.a2,
                      }}
                    >
                      {d.codigo}
                    </td>
                    <td style={{ padding: '2px 4px', fontSize: 10, color: C.b }}>{d.nombre}</td>
                    <td
                      style={{
                        padding: '2px 4px',
                        textAlign: 'center',
                        fontSize: 9,
                        fontFamily: "'JetBrains Mono',monospace",
                        color: C.go,
                      }}
                    >
                      {d.procesoCodigo}
                    </td>
                    <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0 5px',
                          borderRadius: 6,
                          fontSize: 7,
                          fontWeight: 800,
                          background: isPubl ? C.verdeBg : C.ambarBg,
                          color: isPubl ? C.verde : C.ambar,
                        }}
                      >
                        {d.estado === 'DIGITALIZADO'
                          ? 'PUBL'
                          : d.estado === 'DOCUMENTADO'
                            ? 'DOC'
                            : 'BORR'}
                      </span>
                    </td>
                    <td
                      style={{ textAlign: 'center', padding: '4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (proc) navToDocInLevel3(proc.id, d.codigo);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={C.a2}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ cursor: 'pointer', opacity: 0.7 }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as SVGSVGElement).style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as SVGSVGElement).style.opacity = '0.7';
                        }}
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
